import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Image, Grid, Popup, Icon } from 'semantic-ui-react';
import { useSharedData } from '../../hooks/core/useZoneHooks';
import { ActorManagementEnh, PortfolioManagementEnh, CharacterManagementEnh, VariationManagementEnh } from '../management';
import { selectActiveRole } from '../../../slices/userRoleSlice';
import { AuthStdFooter } from "../../../utils/auth-std-footer"

import '../../styles/voiceActorWorksheet.css';
import readerOnStackLarge from '../../../assets/shutterstock_2294366549-1024x1024.png';
// Constants for tab indices
const TAB_INDEX = {
  ACTORS: 0,
  PORTFOLIOS: 1,
  CHARACTERS: 2,
  VARIATIONS: 3
};

// Create a context for tab navigation
export const TabNavigationContext = createContext();

// Hook to use tab navigation
export const useTabNavigation = () => {
  const context = useContext(TabNavigationContext);
  if (!context) {
    throw new Error('useTabNavigation must be used within a TabNavigationProvider');
  }
  return context;
};

/**
 * VoiceActorWorksheetPageEnh component
 * Enhanced version of VoiceActorWorksheetPage that uses the enhanced components
 * @returns {JSX.Element} VoiceActorWorksheetPageEnh component
 */
const VoiceActorWorksheetPageEnh = () => {
  const sharedData = useSharedData();

  // Detect user role and current user info
  const activeRole = useSelector(selectActiveRole);
  const currentUser = useSelector(state => state.user.profile?.data);
  const currentUserId = currentUser?.userId || currentUser?.actorId;

  // Single-actor mode: voiceActor role users only manage themselves (no actor list)
  const isSingleActorMode = activeRole === 'voiceActor';

  // Start on Portfolios tab (index 1) for single-actor mode, Actors tab (index 0) otherwise
  const [activeIndex, setActiveIndex] = useState(isSingleActorMode ? TAB_INDEX.PORTFOLIOS : TAB_INDEX.ACTORS);

  // Extract data from new hook
  const {
    actors,
    portfolios,
    selections
  } = sharedData;

  // Map to interface
  const actorsArray = Array.isArray(actors) ? actors : [];
  const portfoliosArray = Array.isArray(portfolios) ? portfolios : [];
  const selectionsObj = selections || {};

  const selectedActor = actorsArray.find(a => a.uuid === selectionsObj.actorId) || null;
  const selectedPortfolio = portfoliosArray.find(p => p.uuid === selectionsObj.portfolioId) || null;
  const actorId = selectionsObj.actorId;
  const portfolioId = selectionsObj.portfolioId;

  // Build the display name for the selected actor
  const actorDisplayName = useMemo(() => {
    if (isSingleActorMode && currentUser) {
      // For voice actors, always show their own name
      const firstName = currentUser.firstName || '';
      const lastName = currentUser.lastName || '';
      return (firstName + ' ' + lastName).trim() || currentUser.title || 'Voice Actor';
    }
    return selectedActor?.name || 'Unknown';
  }, [isSingleActorMode, currentUser, selectedActor]);

  // For now, we'll use dummy functions for persistSelections since it's not in the new architecture
  const persistSelections = true;
  const setPersistSelections = () => { };

  // Set persistSelections to true when the component mounts
  useEffect(() => {
    console.log('[VA_WORKSHEET] Setting persistSelections to true on mount');
    setPersistSelections(true);

    // Initialize data fetching
    sharedData.actions.fetchActors();

    // Clean up function - only clear selections when truly leaving the worksheet
    return () => {
      console.log('[VA_WORKSHEET] Component unmounting - worksheet is being closed');
    };
  }, []); // Remove dependencies to prevent infinite loop

  // Auto-select actor when actors are loaded
  useEffect(() => {
    console.log('[VA_WORKSHEET] Auto-selection check - actorsArray.length:', actorsArray.length, 'actorId:', actorId, 'singleActorMode:', isSingleActorMode);

    if (actorsArray.length > 0 && !actorId) {
      if (isSingleActorMode && currentUserId) {
        // In single-actor mode, find the actor matching the logged-in user
        const selfActor = actorsArray.find(a =>
          a.uuid === currentUserId || a.userId === currentUserId
        );
        const actorToSelect = selfActor || actorsArray[0];
        console.log('[VA_WORKSHEET] Single-actor mode: auto-selecting self:', actorToSelect.uuid);
        sharedData.actions.setActorSelection(actorToSelect.uuid);
        sharedData.actions.fetchPortfolios(actorToSelect.uuid);
      } else {
        console.log('[VA_WORKSHEET] Auto-selecting first actor:', actorsArray[0].uuid);
        sharedData.actions.setActorSelection(actorsArray[0].uuid);
        sharedData.actions.fetchPortfolios(actorsArray[0].uuid);
      }
    }
  }, [actorsArray, actorId, isSingleActorMode, currentUserId]);

  // Handle tab button click - memoized for stable context value
  const handleTabClick = useCallback((tabIndex) => {
    // Check if trying to navigate to Characters or Variations tabs without a portfolio selected
    if ((tabIndex === TAB_INDEX.CHARACTERS || tabIndex === TAB_INDEX.VARIATIONS) && !portfolioId) {
      // Don't allow navigation to these tabs if no portfolio is selected
      return;
    }

    setActiveIndex(tabIndex);
    setPersistSelections(true);
  }, [portfolioId, setPersistSelections]);

  // Add effect to redirect if needed
  useEffect(() => {
    // If on Characters or Variations tab but no portfolio is selected, redirect to Portfolios tab
    if ((activeIndex === TAB_INDEX.CHARACTERS || activeIndex === TAB_INDEX.VARIATIONS) && !portfolioId) {
      setActiveIndex(TAB_INDEX.PORTFOLIOS); // Portfolios tab index
    }
  }, [activeIndex, portfolioId]);

  // Add CSS styles for the tab navigation
  const styles = {
    tabNavigation: {
      display: 'flex',
      marginBottom: '1rem',
      marginTop: '20px',
      borderBottom: '1px solid #ddd',
      padding: '0 1rem'
    },
    tabButton: {
      marginRight: '0.5rem',
      borderRadius: '0.28571429rem 0.28571429rem 0 0',
      borderBottom: 'none'
    },
    activeTabButton: {
      borderBottom: '2px solid #2185d0'
    },
    tabContent: {
      border: '1px solid #ddd',
      borderTop: 'none',
      padding: '1rem',
      marginTop: '-1px'
    }
  };

  // Phase 2: Memoize the navigation context value to prevent unnecessary re-renders
  const navigationContextValue = useMemo(() => ({
    handleTabClick,
    TAB_INDEX
  }), [handleTabClick]);

  return (
    <TabNavigationContext.Provider value={navigationContextValue}>
      <div className="va-voice-actor-worksheet">
        <div className="va-voice-actor-worksheet-header">
          <Grid verticalAlign="middle">
            <Grid.Row columns={2}>
              <Grid.Column width={4}>
                <div className="header-left">
                  <Image src={readerOnStackLarge} size="tiny" />
                  <h1>Voice Actor Worksheet</h1>
                </div>
              </Grid.Column>
              <Grid.Column width={6} textAlign="left">
                {(selectedActor || isSingleActorMode) && (
                  <div className="selected-entity-info">
                    <span>{isSingleActorMode ? 'Actor' : 'Selected Actor'}: <strong>{actorDisplayName}</strong></span>
                    {selectedPortfolio && (
                      <span> | Portfolio: <strong>{selectedPortfolio.name || selectedPortfolio.title}</strong></span>
                    )}
                  </div>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>

        {/* Custom tab navigation */}
        <div style={styles.tabNavigation}>
          {/* Hide Voice Actors tab in single-actor mode */}
          {!isSingleActorMode && (
            <Button
              primary={activeIndex === TAB_INDEX.ACTORS}
              onClick={() => handleTabClick(TAB_INDEX.ACTORS)}
              icon
              labelPosition="left"
              style={{
                ...styles.tabButton,
                ...(activeIndex === TAB_INDEX.ACTORS ? styles.activeTabButton : {})
              }}
            >
              <Icon name="users" />
              Voice Actors
            </Button>
          )}
          <Button
            primary={activeIndex === TAB_INDEX.PORTFOLIOS}
            onClick={() => handleTabClick(TAB_INDEX.PORTFOLIOS)}
            icon
            labelPosition="left"
            style={{
              ...styles.tabButton,
              ...(activeIndex === TAB_INDEX.PORTFOLIOS ? styles.activeTabButton : {})
            }}
          >
            <Icon name="folder" />
            Portfolios
          </Button>
          <Popup
            trigger={
              <span style={{ display: 'inline-block' }}>
                <Button
                  primary={activeIndex === TAB_INDEX.CHARACTERS}
                  onClick={() => handleTabClick(TAB_INDEX.CHARACTERS)}
                  disabled={!portfolioId}
                  icon
                  labelPosition="left"
                  style={{
                    ...styles.tabButton,
                    ...(activeIndex === TAB_INDEX.CHARACTERS ? styles.activeTabButton : {})
                  }}
                >
                  <Icon name="user" />
                  Characters
                </Button>
              </span>
            }
            content="Select a portfolio first to manage characters"
            position="top center"
            disabled={!!portfolioId}
          />
          <Popup
            trigger={
              <span style={{ display: 'inline-block' }}>
                <Button
                  primary={activeIndex === TAB_INDEX.VARIATIONS}
                  onClick={() => handleTabClick(TAB_INDEX.VARIATIONS)}
                  disabled={!portfolioId}
                  icon
                  labelPosition="left"
                  style={{
                    ...styles.tabButton,
                    ...(activeIndex === TAB_INDEX.VARIATIONS ? styles.activeTabButton : {})
                  }}
                >
                  <Icon name="microphone" />
                  Variations
                </Button>
              </span>
            }
            content="Select a portfolio first to manage variations"
            position="top center"
            disabled={!!portfolioId}
          />
        </div>

        {/* Persistent containers */}
        <div className="va-neg-margin-top" style={styles.tabContent}>
          {/* Only mount ActorManagement in multi-actor mode */}
          {!isSingleActorMode && (
            <div style={{ display: activeIndex === TAB_INDEX.ACTORS ? 'block' : 'none' }}>
              <ActorManagementEnh />
            </div>
          )}
          <div style={{ display: activeIndex === TAB_INDEX.PORTFOLIOS ? 'block' : 'none' }}>
            <PortfolioManagementEnh />
          </div>
          <div style={{ display: activeIndex === TAB_INDEX.CHARACTERS ? 'block' : 'none' }}>
            <CharacterManagementEnh />
          </div>
          <div style={{ display: activeIndex === TAB_INDEX.VARIATIONS ? 'block' : 'none' }}>
            <VariationManagementEnh />
          </div>
        </div>
        {AuthStdFooter()}

        {/* Always-mounted components for data fetching (hidden) */}
        {/* <div style={{ display: 'none' }}>
          Mount CharacterManagement to ensure character fetching happens
          {portfolioId && <CharacterManagementEnh />}
        </div> */}
      </div>
    </TabNavigationContext.Provider>
  );
};

export default VoiceActorWorksheetPageEnh;
